import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Form, Spinner, Table } from 'react-bootstrap';
import { parivartanAPI } from '../../../services/parivartanAPI';
import { platformAPI } from '../../../services/platformAPI';
import { canEditTender } from '../../../utils/tenderRecordAccess';
import AiAssistNote from '../../../components/workspace/AiAssistNote';
import { activeImportValues, keepCurrent, loadOrganisationSettings } from '../../../utils/organisationSettings';
import './TenderEligibilityDesk.scss';

const FALLBACK_CATEGORIES = [
  'Technical',
  'Financial',
  'Experience',
  'Legal',
  'Certification',
  'Personnel',
  'General'
];

const ASSESSMENTS = [
  { value: 'meets', label: 'Meets' },
  { value: 'does_not_meet', label: 'Does not meet' },
  { value: 'needs_review', label: 'Needs review' },
  { value: 'not_applicable', label: 'Not applicable' }
];

const DECISIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'in_review', label: 'In review' },
  { value: 'go', label: 'Go' },
  { value: 'conditional_go', label: 'Conditional go' },
  { value: 'no_go', label: 'No-go' },
  { value: 'deferred', label: 'Deferred' }
];

const blankForm = (category = 'Technical') => ({
  id: '',
  category,
  requirementText: '',
  threshold: '',
  sourcePage: '',
  sourceSection: '',
  sourceVersion: '',
  assessment: 'needs_review',
  gapNotes: '',
  mitigation: ''
});

const labelize = (v) =>
  String(v || '—')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

const fmtDate = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const TenderEligibilityDesk = ({ tenderId, tender, onChanged, focus = 'both', refreshKey = 0 }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [requirements, setRequirements] = useState([]);
  const [summary, setSummary] = useState(null);
  const [decision, setDecision] = useState(null);
  const [history, setHistory] = useState([]);
  const [blockers, setBlockers] = useState([]);
  const [form, setForm] = useState(blankForm());
  const [decisionForm, setDecisionForm] = useState({
    humanDecision: 'in_review',
    decisionRationale: '',
    decisionConditions: '',
    outcomeReasonCode: '',
    outcomeRemarks: ''
  });
  const [reasons, setReasons] = useState([]);
  const [aiNote, setAiNote] = useState(null);
  const [categories, setCategories] = useState(FALLBACK_CATEGORIES);

  const editable = canEditTender(tender);
  const defaultCategory = categories[0] || 'Technical';

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    return Promise.all([
      parivartanAPI.listEligibility(tenderId),
      parivartanAPI.getDecision(tenderId),
      platformAPI.getTenderAssessment(tenderId).catch(() => null)
    ])
      .then(([elRes, dRes, scoreRes]) => {
        const el = elRes.data?.data || {};
        const d = dRes.data?.data || {};
        const assessment = scoreRes?.data?.data?.assessment;
        const withdrawn = assessment?.status === 'returned' && ['go', 'conditional_go', 'no_go'].includes(d.humanDecision);
        const humanDecision = withdrawn ? 'in_review' : (d.humanDecision || 'in_review');
        setRequirements(el.requirements || []);
        setSummary(el.summary || d.eligibilitySummary || null);
        setDecision({ ...d, humanDecision, assessmentWithdrawn: withdrawn });
        setHistory(d.history || []);
        setBlockers(d.blockers || []);
        setDecisionForm({
          humanDecision,
          decisionRationale: withdrawn ? '' : (d.review?.decisionRationale || ''),
          decisionConditions: withdrawn ? '' : (d.review?.decisionConditions || ''),
          outcomeReasonCode: withdrawn ? '' : (d.review?.outcomeReasonCode || ''),
          outcomeRemarks: withdrawn ? '' : (d.review?.outcomeRemarks || '')
        });
      })
      .catch(() => setError('Unable to load eligibility and decision desk'))
      .finally(() => setLoading(false));
  }, [tenderId, refreshKey]);

  useEffect(() => {
    if (decisionForm.humanDecision !== 'no_go') return undefined;
    platformAPI.listReference('outcome-reasons', { outcome: 'no_go', keep: decisionForm.outcomeReasonCode || undefined })
      .then((response) => setReasons(response.data?.data?.values || []))
      .catch(() => setReasons([]));
    return undefined;
  }, [decisionForm.humanDecision, decisionForm.outcomeReasonCode]);

  useEffect(() => {
    let cancelled = false;
    loadOrganisationSettings().then((data) => {
      if (cancelled) return;
      const next = activeImportValues(data?.settings?.importLists?.eligibilityCategories);
      const list = next.length ? next : FALLBACK_CATEGORIES;
      setCategories(list);
      setForm((prev) => (prev.id || prev.requirementText ? prev : blankForm(list[0] || 'Technical')));
    });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const setField = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const suggestNotes = async () => {
    if (!form.requirementText.trim()) {
      setError('Enter the requirement before asking for notes');
      return;
    }
    setSaving(true);
    setError('');
    setAiNote(null);
    try {
      const res = await platformAPI.suggestEligibilityNote(tenderId, {
        requirementText: form.requirementText.trim(),
        threshold: form.threshold
      });
      const data = res.data?.data || {};
      if (data.gapNotes) {
        setForm((prev) => ({
          ...prev,
          gapNotes: data.gapNotes,
          mitigation: data.mitigation || prev.mitigation
        }));
      }
      setAiNote(data);
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to suggest notes');
    } finally {
      setSaving(false);
    }
  };

  const editRow = (row) => {
    setForm({
      id: row._id,
      category: row.category || 'General',
      requirementText: row.requirementText || '',
      threshold: row.threshold || '',
      sourcePage: row.sourcePage || '',
      sourceSection: row.sourceSection || '',
      sourceVersion: row.sourceVersion || '',
      assessment: row.assessment || 'needs_review',
      gapNotes: row.gapNotes || '',
      mitigation: row.mitigation || ''
    });
  };

  const saveRequirement = async () => {
    if (!editable) return;
    if (!form.requirementText.trim()) {
      setError('Requirement text is required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = {
        id: form.id || undefined,
        category: form.category,
        requirementText: form.requirementText.trim(),
        threshold: form.threshold.trim(),
        sourcePage: form.sourcePage.trim(),
        sourceSection: form.sourceSection.trim(),
        sourceVersion: form.sourceVersion.trim(),
        assessment: form.assessment,
        gapNotes: form.gapNotes.trim(),
        mitigation: form.mitigation.trim()
      };
      const res = await parivartanAPI.upsertEligibility(tenderId, payload);
      setRequirements(res.data?.data?.requirements || []);
      setSummary(res.data?.data?.summary || null);
      setForm(blankForm(defaultCategory));
      const d = await parivartanAPI.getDecision(tenderId);
      setDecision(d.data?.data || null);
      setBlockers(d.data?.data?.blockers || []);
      if (onChanged) onChanged();
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to save requirement');
    } finally {
      setSaving(false);
    }
  };

  const removeRequirement = async (id) => {
    if (!editable || !id) return;
    setSaving(true);
    setError('');
    try {
      const res = await parivartanAPI.deleteEligibility(tenderId, id);
      setRequirements(res.data?.data?.requirements || []);
      setSummary(res.data?.data?.summary || null);
      if (form.id === id) setForm(blankForm(defaultCategory));
      const d = await parivartanAPI.getDecision(tenderId);
      setDecision(d.data?.data || null);
      setBlockers(d.data?.data?.blockers || []);
      if (onChanged) onChanged();
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to remove requirement');
    } finally {
      setSaving(false);
    }
  };

  const saveDecision = async () => {
    if (!editable) return;
    setSaving(true);
    setError('');
    try {
      const res = await parivartanAPI.setDecision(tenderId, {
        humanDecision: decisionForm.humanDecision,
        decisionRationale: decisionForm.decisionRationale.trim(),
        decisionConditions: decisionForm.decisionConditions.trim(),
        outcomeReasonCode: decisionForm.humanDecision === 'no_go' ? decisionForm.outcomeReasonCode : '',
        outcomeRemarks: decisionForm.humanDecision === 'no_go' ? decisionForm.outcomeRemarks.trim() : ''
      });
      setDecision({
        ...(res.data?.data || {}),
        humanDecision: res.data?.data?.humanDecision || decisionForm.humanDecision
      });
      setSummary(res.data?.data?.eligibilitySummary || summary);
      await load();
      if (onChanged) onChanged();
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to record decision');
    } finally {
      setSaving(false);
    }
  };

  const showEligibility = focus === 'both' || focus === 'eligibility';
  const showDecision = focus === 'both' || focus === 'decision';

  const kpis = useMemo(() => ([
    { label: 'Requirements', value: summary?.total ?? requirements.length },
    { label: 'Meets', value: summary?.byAssessment?.meets || 0 },
    { label: 'Gaps', value: summary?.gaps || 0 },
    { label: 'Open review', value: summary?.openReview || 0 }
  ]), [summary, requirements.length]);

  if (loading && !requirements.length && !decision) {
    return (
      <div className="telg telg--loading">
        <Spinner animation="border" size="sm" />
      </div>
    );
  }

  return (
    <div className="telg">
      {error ? <Alert variant="warning">{error}</Alert> : null}
      <AiAssistNote result={aiNote} />

      <div className="telg__banner">
        <div>
          <h3>Eligibility & go / no-go</h3>
          <p>
            Capture RFP requirements with source and threshold, assess gaps and mitigations, then
            record the bid decision.
          </p>
        </div>
        <Button size="sm" variant="outline-primary" onClick={load} disabled={loading}>
          Refresh
        </Button>
      </div>

      <div className="telg__kpis">
        {kpis.map((kpi) => (
          <div key={kpi.label} className="telg__kpi">
            <span>{kpi.label}</span>
            <strong>{kpi.value}</strong>
          </div>
        ))}
        <div className="telg__kpi">
          <span>Decision</span>
          <strong>{decision?.assessmentWithdrawn ? 'Returned — not cleared' : labelize(decision?.humanDecision)}</strong>
        </div>
      </div>

      {decision?.conflicts?.length ? (
        <Alert variant="warning" className="small mb-0">
          Legacy decision conflicts detected — human decision on this desk is authoritative.
        </Alert>
      ) : null}

      {showEligibility ? (
        <section className="telg__panel">
          <header className="telg__panel-head">
            <h4>Eligibility requirements</h4>
            <span>{requirements.length} row{requirements.length === 1 ? '' : 's'}</span>
          </header>

          <div className="telg__table-wrap">
            <Table size="sm" hover className="telg__table mb-0">
              <thead>
                <tr>
                  <th>Category</th>
                  <th>Requirement</th>
                  <th>Threshold</th>
                  <th>Source</th>
                  <th>Assessment</th>
                  <th>Gap / mitigation</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {requirements.map((row) => (
                  <tr key={row._id} className={row.decisionStale ? 'is-stale' : undefined}>
                    <td>{row.category}</td>
                    <td>{row.requirementText}</td>
                    <td>{row.threshold || '—'}</td>
                    <td>
                      {[row.sourceSection, row.sourcePage ? `p.${row.sourcePage}` : '', row.sourceVersion ? `v${row.sourceVersion}` : '']
                        .filter(Boolean)
                        .join(' · ') || '—'}
                    </td>
                    <td>
                      <span className={`telg__tag telg__tag--${row.assessment}`}>
                        {labelize(row.assessment)}
                      </span>
                      {row.decisionStale ? <span className="telg__stale"> Stale</span> : null}
                    </td>
                    <td>
                      {row.gapNotes || row.mitigation ? (
                        <>
                          {row.gapNotes ? <div>{row.gapNotes}</div> : null}
                          {row.mitigation ? <div className="telg__mitigation">{row.mitigation}</div> : null}
                        </>
                      ) : '—'}
                    </td>
                    <td className="telg__row-actions">
                      {editable ? (
                        <>
                          <button type="button" onClick={() => editRow(row)}>Edit</button>
                          <button type="button" onClick={() => removeRequirement(row._id)}>Remove</button>
                        </>
                      ) : null}
                    </td>
                  </tr>
                ))}
                {!requirements.length ? (
                  <tr>
                    <td colSpan={7} className="muted">No eligibility requirements yet.</td>
                  </tr>
                ) : null}
              </tbody>
            </Table>
          </div>

          {editable ? (
            <div className="telg__form">
              <header className="telg__form-head">
                <h5>{form.id ? 'Edit requirement' : 'Add requirement'}</h5>
                {form.id ? (
                  <button type="button" onClick={() => setForm(blankForm(defaultCategory))}>Clear</button>
                ) : null}
              </header>
              <div className="telg__fields">
                <Form.Group>
                  <Form.Label>Category</Form.Label>
                  <Form.Select value={form.category} onChange={setField('category')}>
                    {keepCurrent(categories, form.category, FALLBACK_CATEGORIES).map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
                <Form.Group className="telg__span-2">
                  <Form.Label>Requirement</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={form.requirementText}
                    onChange={setField('requirementText')}
                    placeholder="Exact RFP requirement"
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Label>Threshold</Form.Label>
                  <Form.Control
                    value={form.threshold}
                    onChange={setField('threshold')}
                    placeholder="e.g. 5 years / INR 10 Cr"
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Label>Source section</Form.Label>
                  <Form.Control
                    value={form.sourceSection}
                    onChange={setField('sourceSection')}
                    placeholder="Clause / section"
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Label>Source page</Form.Label>
                  <Form.Control
                    value={form.sourcePage}
                    onChange={setField('sourcePage')}
                    placeholder="Page"
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Label>Source version</Form.Label>
                  <Form.Control
                    value={form.sourceVersion}
                    onChange={setField('sourceVersion')}
                    placeholder="RFP rev"
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Label>Assessment</Form.Label>
                  <Form.Select value={form.assessment} onChange={setField('assessment')}>
                    {ASSESSMENTS.map((a) => (
                      <option key={a.value} value={a.value}>{a.label}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
                <Form.Group className="telg__span-2">
                  <Form.Label>Gap notes</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={form.gapNotes}
                    onChange={setField('gapNotes')}
                    placeholder="Where the firm falls short"
                  />
                </Form.Group>
                <Form.Group className="telg__span-2">
                  <Form.Label>Mitigation</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={form.mitigation}
                    onChange={setField('mitigation')}
                    placeholder="How the gap will be closed (JV, sub-consultant, clarification)"
                  />
                </Form.Group>
              </div>
              <div className="telg__actions">
                <Button size="sm" variant="outline-primary" disabled={saving} onClick={suggestNotes}>
                  Suggest notes
                </Button>
                <Button size="sm" disabled={saving} onClick={saveRequirement}>
                  {form.id ? 'Update requirement' : 'Add requirement'}
                </Button>
              </div>
            </div>
          ) : (
            <p className="telg__hint">This pursuit is locked for editing.</p>
          )}
        </section>
      ) : null}

      {showDecision ? (
        <section className="telg__panel">
          <header className="telg__panel-head">
            <h4>Go / no-go decision</h4>
            <span>v{decision?.assessmentVersion || decision?.review?.assessmentVersion || 1}</span>
          </header>
          {decision?.assessmentWithdrawn ? (
            <Alert variant="warning">The weighted assessment was returned. It is not an authorised decision and does not clear this tender to proceed.</Alert>
          ) : null}

          {blockers.length && ['go', 'conditional_go'].includes(decisionForm.humanDecision) ? (
            <Alert variant="light" className="telg__blockers">
              <strong>Before go / conditional go</strong>
              <ul>
                {blockers.map((b) => (
                  <li key={b}>{b}</li>
                ))}
              </ul>
            </Alert>
          ) : null}

          <div className="telg__decision-grid">
            <div className="telg__fields">
              <Form.Group>
                <Form.Label>Decision</Form.Label>
                <Form.Select
                  value={decisionForm.humanDecision}
                  disabled={!editable}
                  onChange={(e) =>
                    setDecisionForm((prev) => ({ ...prev, humanDecision: e.target.value }))
                  }
                >
                  {DECISIONS.map((d) => (
                    <option key={d.value} value={d.value}>{d.label}</option>
                  ))}
                </Form.Select>
              </Form.Group>
              {decisionForm.humanDecision === 'no_go' ? (
                <>
                  <Form.Group>
                    <Form.Label>Reason</Form.Label>
                    <Form.Select value={decisionForm.outcomeReasonCode} disabled={!editable} onChange={(e) => setDecisionForm((prev) => ({ ...prev, outcomeReasonCode: e.target.value }))}>
                      <option value="">Select a reason</option>
                      {reasons.filter((row) => row.status === 'active' || row.code === decisionForm.outcomeReasonCode).map((row) => (
                        <option key={row.code} value={row.code}>{row.label}{row.status === 'inactive' ? ' (inactive)' : ''}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                  <Form.Group className="telg__span-2">
                    <Form.Label>Remarks{decisionForm.outcomeReasonCode === 'other' ? ' (required for Other)' : ''}</Form.Label>
                    <Form.Control value={decisionForm.outcomeRemarks} disabled={!editable} onChange={(e) => setDecisionForm((prev) => ({ ...prev, outcomeRemarks: e.target.value }))} />
                  </Form.Group>
                </>
              ) : null}
              <Form.Group className="telg__span-2">
                <Form.Label>Rationale</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  disabled={!editable}
                  value={decisionForm.decisionRationale}
                  onChange={(e) =>
                    setDecisionForm((prev) => ({ ...prev, decisionRationale: e.target.value }))
                  }
                  placeholder="Why this decision is being taken"
                />
              </Form.Group>
              <Form.Group className="telg__span-2">
                <Form.Label>Conditions (for conditional go)</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  disabled={!editable}
                  value={decisionForm.decisionConditions}
                  onChange={(e) =>
                    setDecisionForm((prev) => ({ ...prev, decisionConditions: e.target.value }))
                  }
                  placeholder="Conditions that must be met to proceed"
                />
              </Form.Group>
            </div>

            <div className="telg__history">
              <h5>Decision history</h5>
              {history.length ? (
                <ul>
                  {history.map((h) => (
                    <li key={h._id}>
                      <strong>{labelize(h.decision || h.action)}</strong>
                      <span>{fmtDate(h.createdAt)}</span>
                      {h.comment ? <p>{h.comment}</p> : null}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="telg__hint">No prior decisions recorded.</p>
              )}
            </div>
          </div>

          {editable ? (
            <div className="telg__actions">
              <Button size="sm" disabled={saving} onClick={saveDecision}>
                Record decision
              </Button>
              <span className="telg__hint">
                Go moves the pursuit into preparation. No-go closes it. Submission still requires go or
                conditional go.
              </span>
            </div>
          ) : null}
        </section>
      ) : null}
    </div>
  );
};

export default TenderEligibilityDesk;
